import EventEmitter from 'events';
import PeerConnection from './PeerConnection';
import Debug from '@/debug';
const debug = Debug('bbc:WebRTCController');

const MAX_PEERS_CONNECTED = parseInt(localStorage.getItem('maxPeers') || 40);

export default class WebRTCController extends EventEmitter {
    constructor(signaling, localMediaTracks = {}, useVideo) {
        debug('constructor()', { signaling, localMediaTracks, useVideo });
        super();

        this.signaling = signaling;
        this.localMediaTracks = localMediaTracks;
        this.useVideo = useVideo;
        this.peers = new Map();

        this.peersAvailable = new MapQueue();
        this.admittedButNotAdded = new Set();

        this.iceServers = [
            { urls: "stun:stun.l.google.com:19302" }
        ];

        this.signaling.on('ICE_SERVERS', (data) => {
            this.iceServers = data;
        });

        this.signaling.on('PEER_JOINED', (data) => {
            this.peersAvailable.set(data.peer_id, data);
            this.admitAvailablePeer();
        });

        this.signaling.on('ADD_PEER', (data) => {
            this.addPeer(data)
        });

        this.signaling.on('REMOVE_PEER', (data) => {
            this.removePeer(data)
        });

        this.signaling.on('SESSION_DESCRIPTION', (data) => {
            let peer = this.peers.get(data.peer_id);
            if (peer) {
                peer.receiveSessionDescription(data.session_description);
            }
        });

        this.signaling.on('ICE_CANDIDATE', (data) => {
            let peer = this.peers.get(data.peer_id);
            if (peer) {
                peer.receiveIceCandidate(data.ice_candidate);
            }
        });

        this.signaling.on('DISCONNECTED', () => {
            this.removeAllPeers();
        });
    }

    /**
     * @param {MediaStreamTrack} track
     */
    set outgoingAudioTrack(track) {
        debug('outgoingAudioTrack()', track);
        if (track instanceof MediaStreamTrack) {
            this.localMediaTracks.audio = track;
            this.peers.forEach((peer) => {
                peer.replaceAudioTrack(this.localMediaTracks.audio);
            });
        }
    }

    /**
     * @param {MediaStreamTrack} track
     */
    set outgoingVideoTrack(track) {
        debug('outgoingVideoTrack()', track);
        if (track instanceof MediaStreamTrack) {
            this.localMediaTracks.video = track;
            this.peers.forEach(peer => {
                peer.replaceVideoTrack(this.localMediaTracks.video);
            });
        }
    }

    addPeer(data) {
        debug('addPeer()', data);
        let { peer_id, should_create_offer, userData } = data;

        this.admittedButNotAdded.delete(peer_id);

        if (this.peers.has(peer_id)) {
            console.warn('Already connected to peer');
            return;
        }

        try {
            let newPeer = new PeerConnection(peer_id, should_create_offer, userData, this.iceServers, this.localMediaTracks, this.useVideo);
            newPeer.on('RELAY_SESSION_DESCRIPTION', (event) => {
                this.signaling.signal('RELAY_SESSION_DESCRIPTION', event);
            })

            newPeer.on('RELAY_ICE_CANDIDATE', (event) => {
                this.signaling.signal('RELAY_ICE_CANDIDATE', event);
            })

            newPeer.on('CONNECTED', (event) => {
                this.emit('NEW_PEER', {
                    'peer_id': event.target.remotePeerId,
                    'stream': event.target.stream,
                    'userData': event.target.userData
                });
            });

            newPeer.on('STATS', (event) => {
                this.emit('STATS', {
                    'peer_id': event.target.remotePeerId,
                    'stats': event.target.peerStats.stats
                });
            })

            newPeer.on('MEDIA', (event) => {
                this.emit('MEDIA', {
                    'peer_id': event.target.remotePeerId,
                    'mediaStream': event.target.stream
                });
            });

            this.peers.set(peer_id, newPeer);
        }
        catch (err) {
            console.error('WebRTCController: Failed to create new PeerConnection', err);
        }
    }

    removePeer(data) {
        debug('removePeer()', data);
        let { peer_id } = data;

        let peer = this.peers.get(peer_id);
        if (peer) {
            peer.close();
            this.peers.delete(peer_id);

            this.emit('REMOVE_PEER', {
                'peer_id': peer_id
            });
        }

        this.peersAvailable.delete(peer_id);    // remove from overflow peers
        this.admitAvailablePeer();
    }

    removeAllPeers() {
        debug('removeAllPeers()');
        this.peers.forEach((peer, peer_id) => {
            this.removePeer({ peer_id: peer_id });
        });
    }

    admitAvailablePeer() {
        debug('admitAvailablePeer()');
        if (this.canAdmit()) {
            let data = this.peersAvailable.shift();
            if (data) {
                this.admitPeer(data.peer_id);
                this.admitAvailablePeer();      // see if we can add some more
            }
        }
    }

    admitPeer(peerId) {
        debug('admitPeer()', peerId);
        this.signaling.signal('ADMIT', { peerId: peerId });
        this.admittedButNotAdded.add(peerId);
    }

    canAdmit() {
        return (this.admittedButNotAdded.size + this.peers.size) < MAX_PEERS_CONNECTED;
    }
}

class MapQueue extends Map {
    shift() {
        if (this.size < 1) return null;
        let key = this.keys().next().value;
        let data = this.get(key);
        if (data) {
            this.delete(key);
            return data;
        }
        return null;
    }
}